import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Button } from "../../../components/SystemComponent/Button";
import { Modal } from "../../../components/SystemComponent/Modal";
import {
  MdExtension,
  MdLayers,
  MdInventory,
  MdCheckCircle,
  MdCancel,
  MdHourglassBottom,
  MdTrendingUp,
  MdTrendingDown,
  MdAdd,
  MdAccountBalance,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

export const CompanyDashboard = () => {
  const { companyId } = useParams();
  const api = useMemo(() => createApi(), []);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Ekstra Hak Tanımlama State
  const [openGrant, setOpenGrant] = useState(false);
  const [packageItems, setPackageItems] = useState<any[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<number | "">("");
  const [grantAmount, setGrantAmount] = useState<number | "">("");
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  const loadData = () => {
    if (!companyId) return;
    setLoading(true);
    api.companyModuleSubscription
      .getDashbordById(Number(companyId))
      .then((r) => {
        if (r?.ok) setData(r.data.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [api, companyId]);

  const openGrantModal = async () => {
    setSelectedItemId("");
    setGrantAmount("");
    setGrantError(null);
    setOpenGrant(true);

    if (packageItems.length === 0) {
      try {
        const r = await api.packageItem.getAll();
        if (r?.ok) setPackageItems(r.data?.data ?? r.data ?? []);
      } catch (e) {
        console.error("Failed to load package items", e);
      }
    }
  };

  const submitGrant = async () => {
    if (!companyId || !selectedItemId || grantAmount === "" || Number(grantAmount) <= 0 || granting) return;

    setGranting(true);
    setGrantError(null);

    try {
      const r = await api.companyPackageItemBalance.create({
        companyId: Number(companyId),
        packageItemId: Number(selectedItemId),
        totalAmount: Number(grantAmount),
        balance: Number(grantAmount),
      });

      if (!r?.ok) {
        throw new Error(r?.message || r?.errors?.[0] || "Hak tanımlama başarısız oldu.");
      }

      setOpenGrant(false);
      loadData();
    } catch (e: any) {
      setGrantError(e?.message || "Beklenmeyen bir hata oluştu.");
    } finally {
      setGranting(false);
    }
  };

  const summary = useMemo(() => {
    if (!data) return null;

    const modules = data.modules ?? [];
    const items = modules.flatMap((m: any) => m.items ?? []);

    const now = new Date();

    return {
      moduleCount: modules.length,
      packageCount: new Set(modules.map((m: any) => m.packageId)).size,
      itemCount: items.length,
      activeModules: modules.filter((m: any) => new Date(m.expireDate) > now)
        .length,
      expiredModules: modules.filter((m: any) => new Date(m.expireDate) <= now)
        .length,
      totalDefined: items.reduce(
        (a: number, b: any) => a + (b.packageDefinedAmount ?? 0),
        0
      ),
      totalUsed: items.reduce(
        (a: number, b: any) => a + (b.usedAmount ?? 0),
        0
      ),
      totalRemaining: items.reduce(
        (a: number, b: any) => a + (b.remainingAmount ?? 0),
        0
      ),
    };
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.modules.flatMap((m: any) =>
      (m.items ?? []).map((i: any) => ({
        moduleName: m.moduleName,
        moduleCode: m.moduleCode,
        moduleType: m.moduleType,
        packageTitle: m.packageTitle,
        expireDate: m.expireDate,
        confirmStatus: m.confirmStatus,
        ...i,
      }))
    );
  }, [data]);

  const columns = useMemo<any[]>(
    () => [
      {
        key: "module",
        title: "Module",
        minWidth: 200,
        accessor: (r: any) =>
          r.moduleName + `(${r.moduleType === 1 ? "Core" : "Extra"})`,
      },
      {
        key: "package",
        title: "Package",
        minWidth: 200,
        accessor: (r: any) => r.packageTitle,
      },
      {
        key: "item",
        title: "Item",
        minWidth: 220,
        accessor: (r: any) => r.packageItemDescription,
      },
      {
        key: "defined",
        title: "Defined",
        minWidth: 100,
        accessor: (r: any) => r.packageDefinedAmount,
      },
      {
        key: "total",
        title: "Total",
        minWidth: 100,
        accessor: (r: any) => r.totalAmount,
      },
      {
        key: "used",
        title: "Used",
        minWidth: 100,
        accessor: (r: any) => r.usedAmount,
      },
      {
        key: "remaining",
        title: "Remaining",
        minWidth: 120,
        accessor: (r: any) => r.remainingAmount,
        render: (_: any, v: number) => (
          <Badge
            label={String(v)}
            color={v > 0 ? "#16A34A" : "#DC2626"}
            bg={v > 0 ? "rgba(22,163,74,.18)" : "rgba(220,38,38,.18)"}
          />
        ),
      },
      {
        key: "expire",
        title: "Expire",
        minWidth: 140,
        accessor: (r: any) => r.expireDate,
        render: (_: any, v: string) => {
          const expired = new Date(v) <= new Date();
          return (
            <Badge
              label={expired ? "Expired" : "Active"}
              color={expired ? "#DC2626" : "#16A34A"}
              bg={expired ? "rgba(220,38,38,.18)" : "rgba(22,163,74,.18)"}
            />
          );
        },
      },
    ],
    []
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={S.header}>
        <div>
          <div style={S.title}>{data?.companyName ?? ""}</div>
          <div style={S.subTitle}>Company Modules & Entitlements</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openGrantModal}>
          Ekstra Hak Tanımla
        </Button>
      </div>
      {summary && (
        <div style={S.badgeCard}>
          <div style={S.badgeGrid}>
            <Badge
              icon={MdExtension}
              label="Modules"
              value={summary.moduleCount}
              color="#6366F1"
              bg="rgba(99,102,241,.18)"
            />
            <Badge
              icon={MdLayers}
              label="Packages"
              value={summary.packageCount}
              color="#7C3AED"
              bg="rgba(124,58,237,.18)"
            />
            <Badge
              icon={MdInventory}
              label="Items"
              value={summary.itemCount}
              color="#0EA5E9"
              bg="rgba(14,165,233,.18)"
            />
            <Badge
              icon={MdCheckCircle}
              label="Active"
              value={summary.activeModules}
              color="#16A34A"
              bg="rgba(22,163,74,.18)"
            />
            <Badge
              icon={MdCancel}
              label="Expired"
              value={summary.expiredModules}
              color="#DC2626"
              bg="rgba(220,38,38,.18)"
            />
            <Badge
              icon={MdTrendingUp}
              label="Total Used"
              value={summary.totalUsed}
              color="#F97316"
              bg="rgba(249,115,22,.18)"
            />
            <Badge
              icon={MdTrendingDown}
              label="Remaining"
              value={summary.totalRemaining}
              color="#22C55E"
              bg="rgba(34,197,94,.18)"
            />
            <Badge
              icon={MdHourglassBottom}
              label="Defined"
              value={summary.totalDefined}
              color="#64748B"
              bg="rgba(100,116,139,.18)"
            />
          </div>
        </div>
      )}

      <Table
        tableKey="company-dashboard"
        loading={loading}
        data={rows}
        columns={columns}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={openGrant} onClose={() => (!granting ? setOpenGrant(false) : null)}>
        <div style={S.modalTitle}>Ekstra Hak / Bakiye Tanımla</div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 12 }}>
          Firma: <strong>{data?.companyName}</strong> için ek hak (USERCOUNT, LISTING vb.) tanımlayın.
        </div>

        <div style={S.form}>
          <label style={S.label}>Paket Kalemi (Hak Türü)</label>
          <select
            value={selectedItemId}
            onChange={(e) => setSelectedItemId(Number(e.target.value))}
            style={S.input}
            disabled={granting}
          >
            <option value="">Kalem seçiniz...</option>
            {packageItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.code ? `${item.code}${item.description ? ` - ${item.description}` : ""}` : (item.name || `Item #${item.id}`)}
              </option>
            ))}
          </select>

          <label style={S.label}>Miktar (Bakiye)</label>
          <input
            type="number"
            min={1}
            placeholder="Örn: 5"
            value={grantAmount}
            onChange={(e) => setGrantAmount(Number(e.target.value))}
            style={S.input}
            disabled={granting}
          />

          {grantError && <div style={S.error}>{grantError}</div>}
        </div>

        <div style={S.actions}>
          <Button variant="ghost" onClick={() => setOpenGrant(false)} disabled={granting}>
            İptal
          </Button>
          <Button
            variant="primary"
            onClick={submitGrant}
            loading={granting}
            disabled={granting || !selectedItemId || !grantAmount}
          >
            Tanımla ve Kaydet
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  badgeCard: {
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  badgeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    color: "#0F172A",
  },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 6,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
